"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MeetTheAdvocates() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const team = [{
    name: "SARAH JENKINS",
    role: "PROGRAM DIRECTOR",
    bio: "Sarah leads our respite initiatives with a heart for neurodiverse families, ensuring every caregiver finds the support and community they deserve.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/team_sarah_jenkins_portrait.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/team_sarah_jenkins_portrait.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/team_sarah_jenkins_portrait.webp-wvc-sizes",
    offset: "md:translate-y-0"
  }, {
    name: "MARCUS CHEN",
    role: "ADVOCACY LEAD",
    bio: "Marcus bridges the gap between families and essential resources, navigating complex systems to secure the advocacy our community needs to thrive.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/team_marcus_chen_portrait.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/team_marcus_chen_portrait.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/team_marcus_chen_portrait.png-wvc-sizes",
    offset: "md:translate-y-[60px]"
  }, {
    name: "ELENA RODRIGUEZ",
    role: "BILINGUAL OUTREACH",
    bio: "Elena ensures our resources reach every corner of the neighborhood, providing vital bilingual support to bridge language barriers for all families.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/team_elena_rodriguez_portrait.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/team_elena_rodriguez_portrait.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/team_elena_rodriguez_portrait.webp-wvc-sizes",
    offset: "md:translate-y-[30px]"
  }];
  const collective = ["+ DAVID PARK, EVENT COORDINATOR", "+ MAYA SINGH, FAMILY LIAISON", "+ JORGE LUNA, PARTNERSHIP MANAGER"];
  return <section data-section-id="2889" id="meet-the-advocates" className="relative bg-background text-foreground py-32 px-6 md:px-12 overflow-hidden">
      {}
      <div className="hidden lg:block absolute left-6 top-40 -rotate-90 origin-left">
        <span className="font-default text-xs tracking-widest text-muted-foreground">
          TEAM / 02
        </span>
      </div>

      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`mb-24 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center gap-3 mb-8">
            <span className="font-default text-xs tracking-widest text-secondary">
              02
            </span>
            <span className="h-px w-12 bg-border" />
            <span className="font-default text-xs tracking-widest text-muted-foreground">
              THE HEARTS BEHIND HOPE
            </span>
          </div>

          <h2 className="font-default font-bold uppercase leading-[0.9] tracking-tight text-foreground" style={{
          fontSize: "clamp(3.5rem, 10vw, 9rem)"
        }}>
            THE
            <br />
            <span className="font-serif italic font-normal text-foreground">
              ADVOCATES
            </span>
          </h2>

          <p className="mt-10 max-w-2xl font-default text-xl md:text-[1.25rem] leading-relaxed text-muted-foreground">
            A dedicated team serving neurodiverse families through respite, advocacy, and community connection. We are here to ensure no family walks their journey alone.
          </p>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 lg:gap-12">
          {team.map((member, i) => <div key={i} data-index={i} className={`group transition-all duration-700 ease-out ${member.offset} ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          transitionDelay: `${i * 120}ms`
        }}>
              {}
              <div className="relative overflow-hidden bg-muted transition-shadow duration-300 rounded-lg group-hover:shadow-xl">
                <div className="aspect-[3/4] w-full overflow-hidden">
                  <img src={member.image} data-wvc-srcset={member.srcset} data-wvc-sizes={member.sizes} alt={`${member.name} — ${member.role}`} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale transition-all duration-500 group-hover:contrast-125 group-hover:saturate-150" />
                </div>
              </div>

              {}
              <div className="mt-10">
                <p className="font-default text-xs tracking-widest text-secondary uppercase mb-4">
                  {member.role}
                </p>
                <h3 className="font-default font-bold uppercase text-foreground text-[1.75rem] leading-tight tracking-tight transition-transform duration-300 group-hover:translate-x-1">
                  {member.name}
                </h3>
                <div className="h-px w-full bg-border my-5" />
                <p className="font-default text-base leading-relaxed text-muted-foreground line-clamp-3">
                  {member.bio}
                </p>

                {}
                <div className="mt-6 flex items-center gap-6">
                  <Link to="/OurStoryPage" className="font-default text-xs tracking-widest uppercase text-foreground hover:text-secondary transition-colors duration-200 inline-flex items-center gap-1.5">
                    OUR STORY <ArrowUpRight className="size-3" />
                  </Link>
                  <Link to="/ConnectPage" className="font-default text-xs tracking-widest uppercase text-foreground hover:text-secondary transition-colors duration-200 inline-flex items-center gap-1.5">
                    EMAIL <ArrowUpRight className="size-3" />
                  </Link>
                </div>
              </div>
            </div>)}
        </div>

        {}
        <div className="mt-32">
          <div className="h-px w-full bg-border mb-8" />
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <span className="font-default text-xs tracking-widest text-muted-foreground uppercase">
              The Collective
            </span>
            <div className="flex flex-col md:flex-row md:items-center flex-wrap gap-x-8 gap-y-3">
              {collective.map((person, i) => <span key={i} data-index={i} className="font-default text-xs tracking-widest uppercase text-foreground/80 hover:text-secondary transition-colors duration-200">
                  {person}
                </span>)}
            </div>
          </div>
        </div>

        {}
        <div className="mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="font-default text-lg text-muted-foreground max-w-md">
            Interested in joining our mission or learning more about our programs?
          </p>
          <Button asChild className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-default text-xs tracking-widest uppercase h-12 px-8 rounded-lg">
            <Link to="/ConnectPage">
              Get in touch <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}